import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { getDaysTheOccurrenceIsVisibleOn, getOccurrencesFromEvents } from "../internals/utils/event-utils.mjs";
import { useAdapterContext } from "../use-adapter-context/index.mjs";
import { useEventCalendarStoreContext } from "../use-event-calendar-store-context/index.mjs";
import { schedulerEventSelectors, schedulerOtherSelectors, schedulerResourceSelectors } from "../scheduler-selectors/index.mjs";

/**
 * Gets all the event occurrences for the given days.
 * For recurring events, it expands them to get all the occurrences that fall within the given days.
 * It should be called once per view to get the occurrences for all the visible days in one go.
 * The returned value is a Map where the key is the day key and the value is the list of occurrences for that day.
 */
export function useEventOccurrencesGroupedByDay(parameters) {
  const {
    days
  } = parameters;
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const events = useStore(store, schedulerEventSelectors.processedEventList);
  const visibleResources = useStore(store, schedulerResourceSelectors.visibleMap);
  const displayTimezone = useStore(store, schedulerOtherSelectors.displayTimezone);
  const recurringEventsPlugin = useStore(store, schedulerOtherSelectors.recurringEventsPlugin);
  return React.useMemo(() => innerGetEventOccurrencesGroupedByDay({
    adapter,
    days,
    events,
    visibleResources,
    displayTimezone,
    recurringEventsPlugin
  }), [adapter, days, events, visibleResources, displayTimezone, recurringEventsPlugin]);
}
/**
 * Do not use directly, use the `useEventOccurrencesGroupedByDay` hook instead.
 * This is only exported for testing purposes.
 */
export function innerGetEventOccurrencesGroupedByDay(parameters) {
  const {
    adapter,
    days,
    events,
    visibleResources,
    displayTimezone,
    recurringEventsPlugin
  } = parameters;
  const occurrenceMap = new Map(days.map(day => [day.key, []]));
  const start = adapter.startOfDay(days[0].value);
  const end = adapter.endOfDay(days[days.length - 1].value);
  const occurrences = getOccurrencesFromEvents({
    adapter,
    start,
    end,
    events,
    visibleResources,
    displayTimezone,
    recurringEventsPlugin
  });
  for (const occurrence of occurrences) {
    const eventDays = getDaysTheOccurrenceIsVisibleOn(occurrence, days, adapter);
    for (const dayKey of eventDays) {
      occurrenceMap.get(dayKey).push(occurrence);
    }
  }
  return occurrenceMap;
}